import "./ClientInformationDrawer.scss";

import cx from "classnames";
import { ControlledTextArea } from "components/inputs/TextArea/ControlledTextArea";
import LoadingIcon from "components/Loading/LoadingIcon";
import type { LienFile } from "pages/liens/LienTypes";
import { useForm } from "react-hook-form";
import { useGetInstructionsQuery } from "services/client-instructions-api";
import type {
  AmendedReviewScreeningType,
  CostProjectionDataType,
  MSADataType,
  ClientType,
  FileType,
} from "types/DataTypes";
import { useSelector } from "utils/redux";
import { useEffect } from "react";

type ClientInformationDrawerProps = {
  service:
    | MSADataType
    | CostProjectionDataType
    | AmendedReviewScreeningType
    | LienFile
    | FileType
    | null
    | undefined;
};

type FormValues = {
  communications_instructions: string;
};

const ClientInformationDrawer = ({ service }: ClientInformationDrawerProps) => {
  const showClientInfoDrawer = useSelector(
    (state) => state.drawer.showClientInfoDrawer,
  );

  // handle different service types that might have client info in different places
  const getClientFromService = (
    service: ClientInformationDrawerProps["service"],
  ): ClientType | null => {
    if (!service) {
      return null;
    }

    // MSADataType, CostProjectionDataType, and LienFile have client directly
    if ("client" in service && service.client) {
      return service.client;
    }

    // AmendedReviewScreeningType has client in file.client
    if ("file" in service && service.file && "client" in service.file) {
      return service.file.client;
    }

    return null;
  };

  const client = getClientFromService(service);

  // fetch client instructions
  const { data: clientInstructions, isLoading: isLoadingInstructions } =
    useGetInstructionsQuery(
      {
        clientId: client?.id,
      },
      { skip: !client?.id },
    );

  const {
    control,
    formState: { errors },
    reset,
  } = useForm<FormValues>({
    defaultValues: {
      communications_instructions: "",
    },
  });

  useEffect(() => {
    if (client?.communications_instructions) {
      reset({
        communications_instructions: client.communications_instructions,
      });
    }
  }, [client?.communications_instructions, reset]);

  if (!client) {
    return null;
  }

  return (
    <div
      className={cx("documents client-info-drawer flex jc-se", {
        open: showClientInfoDrawer,
      })}
    >
      <div className="documents-wrapper">
        <div className="swim-wrapper h-100">
          <div className="swim-headers">Client Information</div>

          <div className="data-values">
            <div className="data-block">
              <div className="data-block-header">Special Instructions</div>
              <div className="grid-layout gl-1 notes-grid">
                {isLoadingInstructions ? (
                  <LoadingIcon size="small" />
                ) : clientInstructions?.results &&
                  clientInstructions.results.length > 0 ? (
                  <div>
                    {clientInstructions.results.map((instruction) => (
                      <div key={instruction.id} className="instruction-item">
                        <p>{instruction.instructions}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="empty-state">
                    No special instructions available
                  </p>
                )}
              </div>
            </div>

            <div className="data-block">
              <div className="data-block-header">Custom Service Names</div>
              <div className="grid-layout gl-1 notes-grid">
                {client.custom_services && client.custom_services.length > 0 ? (
                  <div>
                    {client.custom_services.map((customService, index) => (
                      <div key={index} className="service-item">
                        <div className="service-name">
                          {customService.service}:
                        </div>
                        <div className="service-value">
                          {customService.name}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="empty-state">
                    No custom service names configured
                  </p>
                )}
              </div>
            </div>

            <div className="data-block">
              <div className="data-block-header">
                Communication Instructions
              </div>
              <div className="grid-layout gl-1 notes-grid">
                {client.communications_instructions ? (
                  <ControlledTextArea
                    control={control}
                    name="communications_instructions"
                    errors={errors}
                    readOnly={true}
                    advancedEdit={true}
                    onSave={async () => {}} // no-op since it's read-only
                  />
                ) : (
                  <p className="empty-state">
                    No communication instructions available
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientInformationDrawer;
